import { IndianRupee, LockKeyhole } from "lucide-react";

export default function OrderSummary({ packageData, onSubmit }) {
  const {
    package_name = "Default Package",
    original_price = 0,
    installments = [],
    sub_service_id,
  } = packageData || {};

  const priceSummary = {
    originalPrice: original_price,
    installments: installments.map((inst, index) => ({
      id: index + 1,
      label: `Installment ${index + 1}`,
      amount: inst[`installment_${index + 1}_amount`] || 0,
      active: inst.active || false,
    })),
    discount: 0,
    total:
      installments?.length > 0
        ? (() => {
            const activeInst = installments.find((inst) => inst.active);
            return (
              activeInst?.[
                `installment_${installments.indexOf(activeInst) + 1}_amount`
              ] ||
              installments[0][`installment_1_amount`] ||
              original_price ||
              0
            );
          })()
        : original_price || 0,
    courses: 1,
  };

  return (
    <section className="mx-auto max-w-xl rounded-2xl  bg-white p-4 ">
      <h1 className="font-siromi text-[24px] font-normal text-purple-800 leading-[100%] tracking-[0px] align-middle">
        Order Summary
      </h1>

      {/* Package Details */}
      <div className="mt-6 rounded-xl border border-purple-200 bg-purple-50/60 p-5">
        <div className="mb-4 flex items-center gap-2">
          {/* gift icon */}
          <img
            src="/SAPImages/checkout/gift.svg"
            alt="gift"
            className="h-6 w-auto"
          />
          <h2 className="font-siromi text-[18px] font-normal text-[#572575] leading-[100%] tracking-[0px] align-middle">
            Package Details
          </h2>
        </div>

        <dl className="grid grid-cols-1 gap-2 text-lg text-gray-900 sm:grid-cols-3">
          <dt className="sm:col-span-1 font-altisunisa text-[14px] font-normal text-gray-800 leading-[100%] tracking-[0px] align-middle">
            Package :
          </dt>

          <dd className="sm:col-span-2 text-right font-altisunisa text-[14px] font-normal text-[#9D0385] leading-[100%] tracking-[0px] align-middle">
            {package_name}
          </dd>

          <dt className="sm:col-span-1 font-altisunisa text-[14px] font-normal text-gray-800 leading-[100%] tracking-[0px] align-middle">
            Program :
          </dt>

          <dd className="sm:col-span-2 text-right font-altisunisa text-[14px] font-normal text-[#9D0385] leading-[100%] tracking-[0px] align-middle">
            {sub_service_id == 13
              ? "Scholars Undergraduate Admissions Program"
              : "Scholars Postgraduate Admissions Program"}
          </dd>

          <dt className="sm:col-span-1 font-altis text-[14px] font-normal text-gray-800 leading-[100%] tracking-[0px] align-middle">
            Installment :
          </dt>
          <dd className="sm:col-span-2 text-right font-altisunisa text-[14px] font-normal text-[#9D0385] leading-[100%] tracking-[0px] align-middle">
            {installments.length === 0 ? "No" : installments.length}&nbsp;
            Installments
          </dd>
        </dl>

        {/* Installment Pills */}
        <div
          className={`mt-5 grid gap-2
    ${priceSummary.installments.length === 1 ? "grid-cols-1" : ""}
    ${priceSummary.installments.length === 2 ? "sm:grid-cols-2" : ""}
    ${priceSummary.installments.length === 3 ? "sm:grid-cols-3" : ""}
    ${priceSummary.installments.length >= 4 ? "sm:grid-cols-4" : ""}
  `}
        >
          {priceSummary.installments.map((item, index) => (
            <button
              key={index}
              type="button"
              className="rounded-xl bg-white px-1 py-3 text-center shadow-sm"
            >
              <span
                className={`block font-altisunisa text-[12px] ${
                  item.active ? "text-[#9D0385]" : "text-gray-900"
                }`}
              >
                INR {item.amount}
              </span>
              <span className="mt-2 block font-altisunisa text-[12px] text-gray-700">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5">
        <ul className="space-y-3 ">
          {/* Original Price */}
          <li className="flex items-center justify-between">
            <span className="text-gray-800 font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle">
              Original Price
            </span>
            <span className="font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle tabular-nums text-gray-900">
              ₹{priceSummary.originalPrice}
            </span>
          </li>

          {/* Current Installment Only */}
          {(() => {
            // find active installment, or fall back to first
            const activeOrFirst =
              priceSummary.installments.find((inst) => inst.active) ||
              priceSummary.installments[0];

            // if there’s no installment data at all
            if (!activeOrFirst) return null;

            return (
              <li
                key={activeOrFirst.id}
                className="flex items-center justify-between"
              >
                <span
                  className={`font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle ${
                    activeOrFirst.active ? "text-[#9D0385]" : "text-gray-800"
                  }`}
                >
                  {activeOrFirst.label}
                </span>
                <span
                  className={`font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle tabular-nums ${
                    activeOrFirst.active ? "text-[#9D0385]" : "text-gray-900"
                  }`}
                >
                  ₹ {activeOrFirst.amount}
                </span>
              </li>
            );
          })()}

          {/* Discount */}
          <li className="flex items-center justify-between">
            <span className="text-gray-800 font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle">
              Discount
            </span>

            <span className="font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle  tabular-nums text-gray-900">
              ₹ {priceSummary.discount}
            </span>
          </li>

          {/* Divider */}
          <li>
            <hr className="my-2 border-gray-200" />
          </li>

          {/* Total */}
          <li className="flex items-center justify-between">
            <span className="font-altisunisa text-[14px] font-bold leading-[100%] tracking-[0px] align-middle text-[#572575]">
              Total
              <span className="ml-1 text-gray-600 font-altisunisa text-[14px] font-normal leading-[100%] tracking-[0px] align-middle ">
                {/* ({priceSummary.courses} course */}
                {/* {priceSummary.courses > 1 ? "s" : ""}) */}
              </span>
            </span>
            <span className="font-altisunisa tabular-nums font-extrabold text-[#572575]">
              ₹ {priceSummary.total}
            </span>
          </li>
        </ul>
      </div>

      {/* Terms */}
      <p className="font-altisunisa text-[13px] font-normal leading-[150%] tracking-[0px] align-middle mt-5 text-[#777777] mb-2 py-2">
        By completing your purchase, you acknowledge that you have read and
        agree to our
        <a
          href="/termsAndConditions"
          target="_blank"
          rel="noopener noreferrer"
          className="mx-1 font-altisunisa text-[13px] font-normal leading-[100%] tracking-[0px] align-middle text-[#572575] underline underline-offset-0 decoration-solid decoration-[0%] decoration-skip-ink-auto "
        >
          Terms of Use and Privacy Policy .
        </a>
      </p>

      {/* CTA */}
      <button
        type="button"
        className="mt-2 inline-flex w-full items-center justify-center gap-3 rounded-lg bg-[#572575] px-6 py-2 text-[16px] font-altisunisa  leading-[100%] tracking-[0px] align-middle text-white shadow-md hover:bg-[#572575] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#572575]"
        onClick={onSubmit}
      >
        <LockKeyhole className=" w-[14px] h-[18px] relative opacity-100" />
        Proceed To pay
      </button>
    </section>
  );
}
